import { useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { User, Mail, Phone, Cake, Building2, Save, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { API } from "../lib/api";
const ax = () => axios.create({ withCredentials: true });
const ROLES = {
  admin: "Administrador",
  administrador_total: "Administrador total",
  autor_area: "Autor da area",
  gerente: "Gerente",
  colaborador: "Colaborador",
};

export default function ProfilePage() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", birth_date: user?.birth_date || "" });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await ax().put(`${API}/auth/profile`, form);
      setUser(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  if (!user) return null;

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-2 mb-6">
        <User className="w-5 h-5 text-[#2346ff]" />
        <h1 className="text-xl font-bold font-heading text-slate-900">Meu Perfil</h1>
      </div>

      {/* Profile header */}
      <div className="bento-card p-6 mb-5">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white text-2xl font-bold" style={{ background: "linear-gradient(135deg, #2346ff, #1d3ace)" }}>
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-2xl object-cover" />
            ) : (
              user.name.charAt(0)
            )}
          </div>
          <div>
            <h2 className="text-xl font-bold font-heading text-slate-900">{user.name}</h2>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge className="bg-[#e8ecff] text-[#2346ff] text-xs">{ROLES[user.role] || user.role}</Badge>
              {user.department && <Badge variant="secondary" className="text-xs">{user.department}</Badge>}
            </div>
            <p className="text-sm text-slate-400 mt-0.5">{user.email}</p>
          </div>
        </div>
      </div>

      {/* Edit form */}
      <div className="bento-card p-5 space-y-4">
        <h3 className="font-semibold font-heading text-slate-800">Editar Informações</h3>

        <div>
          <Label className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Nome completo</Label>
          <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="mt-1" />
        </div>

        <div>
          <Label className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> E-mail</Label>
          <Input value={user.email} disabled className="mt-1 bg-slate-50" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Telefone</Label>
            <Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} placeholder="(11) 00000-0000" className="mt-1" />
          </div>
          <div>
            <Label className="flex items-center gap-1.5"><Cake className="w-3.5 h-3.5" /> Data de Nascimento</Label>
            <Input type="date" value={form.birth_date} onChange={e => setForm(f => ({...f, birth_date: e.target.value}))} className="mt-1" />
          </div>
        </div>

        <div>
          <Label className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> Departamento</Label>
          <Input value={user.department || "—"} disabled className="mt-1 bg-slate-50" />
          <p className="text-xs text-slate-400 mt-1">O departamento é gerenciado pelo administrador</p>
        </div>

        <div className="flex items-center justify-between pt-2">
          <Button onClick={handleLogout} variant="outline" className="text-red-500 border-red-200 hover:bg-red-50 gap-2">
            <LogOut className="w-4 h-4" /> Sair
          </Button>
          <Button onClick={save} disabled={saving} style={{ backgroundColor: saved ? "#10b981" : "#2346ff" }} className="text-white gap-2 transition-colors">
            <Save className="w-4 h-4" />
            {saving ? "Salvando..." : saved ? "Salvo!" : "Salvar Alterações"}
          </Button>
        </div>
      </div>
    </div>
  );
}
